import React, { FC, useState } from 'react';
import HeaderLogo from './HeaderLogo';
import { SgwtAccountCenter } from './AccountCenter';
import { Link, matchPath, NavLink, useLocation } from 'react-router';
import { RoutePaths } from 'routes/RoutePaths';
import { buildBoardUrlByConfig } from 'services/externalLinksService';
import { LoadingPage } from 'components/common/Loading';
import { useGetAppContextSubscription } from 'queries/AppContextSubscription/appContextSubscription.queries';
import { useGetUserInfo } from 'queries/UserInfo/userInfo.queries';
import UserHeader from './UserHeader';
import classNames from 'classnames';
import { configuration } from 'config/constants';
import { Collapse } from 'react-bootstrap';
import { hasAnyRight, Permissions } from 'services/RightsService';
import LaunchIcon from '@mui/icons-material/Launch';
import { LanguageSettings } from './LanguageSettings';

export const Header: FC = () => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const otherUser = query.get('icId') || undefined;

    const { data: appContextSubscription, isFetching: isFetchingAppContextSubscription } = useGetAppContextSubscription();
    const { data: user, isFetching: isFetchingUser } = useGetUserInfo(otherUser);

    const currentUser = user ?? appContextSubscription?.loggedUser;

    const showLanguageSettings = matchPath({
        path: RoutePaths.Subscriptions.Manage.route,
        end: true,
    }, location.pathname)
        || matchPath({
            path: RoutePaths.Subscriptions.AllActive.route,
            end: true,
        }, location.pathname);

    const hasMenuItems = hasAnyRight(appContextSubscription?.loggedUser, [Permissions.MANAGE_CONTACT_SUBSCRIPTIONS]);
    const [isMiniMenuOpen, setIsMiniMenuOpen] = useState(false);

    if (isFetchingAppContextSubscription || isFetchingUser) {
        return <LoadingPage />;
    };

    return <header className="bg-lvl1">
        <nav className="navbar">
            <div className="navbar-title align-items-center">
                <HeaderLogo otherUser={otherUser} />
                {hasMenuItems && <div className="d-md-none">
                    <button id="mini-menu" className="btn btn-flat" onClick={() => setIsMiniMenuOpen(prev => !prev)}>
                        <i className="icon">menu</i>
                    </button>
                </div>}
            </div>

            <div className="navbar-content">
                {hasMenuItems && <Menu />}
                <div className="d-block d-flex flex-grow-1 mx-3 align-items-center justify-content-end">
                    <SgwtAccountCenter />
                </div>
            </div>
            {hasMenuItems && <Collapse in={isMiniMenuOpen}>
                <div className="w-100 top-100 position-absolute navbar-menu-dropdown dropdown-menu bg-lvl2">
                    <Menu isMobile={true} />
                </div>
            </Collapse>}
        </nav>
        <UserHeader>
            {showLanguageSettings && <div className="d-flex gap-2">
                <LanguageSettings icId={currentUser?.id || ''} />
            </div>}
        </UserHeader>
    </header>;
};

const Menu: FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const otherUser = query.get('icId') || undefined;

    const { data: appContextSubscription } = useGetAppContextSubscription();
    const { data: user } = useGetUserInfo(otherUser);

    const currentUser = user ?? appContextSubscription?.loggedUser;
    const researchContractLink = configuration.externalLinks.researchContract;

    return <ul className={classNames({
        'list-group': isMobile,
        'navbar-navigation m-0': !isMobile,
    })}>
        {hasAnyRight(appContextSubscription?.loggedUser, [Permissions.MANAGE_CONTACT_SUBSCRIPTIONS]) && currentUser && <li className="navbar-item">
            <Link target="_blank"
                to={`${researchContractLink}/third-parties-access?icId=${currentUser.id}`}
                className="navbar-link gap-2">Third Parties <LaunchIcon color="action" fontSize="small" />
            </Link>
        </li>}
        {hasAnyRight(appContextSubscription?.loggedUser, [Permissions.MANAGE_CONTACT_SUBSCRIPTIONS]) && <li className="navbar-item">
            <NavLink
                to={RoutePaths.History.url(1, otherUser || undefined)}
                className={({ isActive }) => classNames('navbar-link', {
                    active: isActive,
                    'd-flex justify-content-between': isMobile,
                })}
            >
                {RoutePaths.History.title}
            </NavLink>
        </li>}
        {hasAnyRight(appContextSubscription?.loggedUser, [Permissions.MANAGE_CONTACT_SUBSCRIPTIONS]) && <li className="navbar-item">
            <Link target="_blank"
                to={buildBoardUrlByConfig(currentUser?.id || '')}
                className="navbar-link gap-2">
                Readership statistics <LaunchIcon color="action" fontSize="small" />
            </Link>
        </li>}
        {hasAnyRight(appContextSubscription?.loggedUser, [Permissions.MANAGE_CONTACT_SUBSCRIPTIONS]) && currentUser?.contractLink && <li className="navbar-item">
            <Link target="_blank"
                to={`${researchContractLink}/?contactIcIds=${currentUser.id}&status=SIGNED`}
                className="navbar-link gap-2">Link to MiFID contract <LaunchIcon color="action" fontSize="small" />
            </Link>
        </li>}
    </ul>;
};
